import {
  AUTH_TOKEN_KEY,
  ensureAccessToken,
  isSessionDead,
  notifySessionExpired,
  refreshAccessToken,
} from '@/lib/authRefresh';
import { isImpersonating } from '@/lib/impersonation';
import { notify } from '@/lib/notify';

/**
 * Shared fetch wrapper for protected API calls.
 * Automatically adds JWT from localStorage and credentials.
 * On 401, attempts token refresh and retries once.
 * Skips refresh while impersonating (impersonation JWT has no refresh cycle).
 */

export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  const impersonating = isImpersonating();
  let token: string | null = localStorage.getItem(AUTH_TOKEN_KEY);

  if (!impersonating) {
    const ensured = await ensureAccessToken();
    token = ensured.ok ? ensured.token : localStorage.getItem(AUTH_TOKEN_KEY);
  }

  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  let res = await fetch(url, { ...init, headers, credentials: 'include' as RequestCredentials });

  if (res.status === 423) {
    const data = await res.clone().json().catch(() => ({}));
    if (data?.type === 'IMPERSONATION_READ_ONLY') {
      notify.error(
        typeof data.error === 'string'
          ? data.error
          : 'Writes are disabled while impersonating. Enable write mode to proceed.'
      );
    }
  }

  // Retry with refresh even when no Bearer yet — HttpOnly cookie may still be valid.
  if (res.status === 401 && !impersonating) {
    const refreshed = await refreshAccessToken();
    if (refreshed.ok) {
      headers.set('Authorization', `Bearer ${refreshed.token}`);
      res = await fetch(url, { ...init, headers, credentials: 'include' as RequestCredentials });
    } else if (refreshed.reason === 'unauthorized' && isSessionDead()) {
      notifySessionExpired();
    }
  }

  return res;
}
